/**
 * Typed client for /discord-prospects/* — backs the Discord Prospects screen
 * (the Discord-channel counterpart of the Apollo Onboarding Pipeline). These
 * endpoints return plain JSON (no { success, data } envelope), so no unwrap().
 */
import { client } from './client';

export type DiscordProspectStatus =
  | 'discovered'
  | 'qualified'
  | 'contacted'
  | 'responded'
  | 'onboarding'
  | 'signed'
  | 'rejected'
  | 'disqualified';

export const DISCORD_PROSPECT_STATUSES: DiscordProspectStatus[] = [
  'discovered',
  'qualified',
  'contacted',
  'responded',
  'onboarding',
  'signed',
  'rejected',
  'disqualified'
];

export interface DiscordOwnerContact {
  platform?: string;
  handle?: string;
  url?: string;
  confidence?: 'low' | 'medium' | 'high';
  note?: string;
}

export interface DiscordProspect {
  _id: string;
  serverId: string;
  serverName: string;
  inviteUrl?: string;
  inviteCode?: string;
  directoryUrl?: string;
  memberCount?: number | null;
  onlineCount?: number | null;
  tags?: string[];
  description?: string;
  bumpedAt?: string | null;
  usesUnbelievaboat?: boolean;
  economySignals?: string[];
  score?: number;
  scoreBreakdown?: Record<string, number> | null;
  status: DiscordProspectStatus;
  ownerContact?: DiscordOwnerContact;
  draftedOutreach?: string;
  source?: string;
  sourceTag?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DiscordOverviewResponse {
  statuses: Partial<Record<DiscordProspectStatus, number>>;
  totalProspects: number;
  qualifiedOrBeyond: number;
  usingUnbelievaboat: number;
  enabled: boolean;
  apifyConfigured: boolean;
}

export interface DiscordConfigResponse {
  enabled: boolean;
  apify: { mode: 'live' | 'stub'; maxResultsCap: number };
  tags: string[];
  contactThreshold: number;
}

export interface DiscordProspectListResponse {
  docs: DiscordProspect[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
}

export interface DiscordProspectListParams {
  page?: number;
  limit?: number;
  status?: DiscordProspectStatus | '';
  source?: string;
  minScore?: number | '';
  usesUnbelievaboat?: 'true' | 'false' | '';
  q?: string;
  sort?: string;
}

export interface DiscordScrapeInput {
  dryRun?: boolean;
  tags?: string[];
  maxResults?: number;
}

export interface DiscordScrapeResult {
  ok: boolean;
  live: boolean;
  result: {
    discovered: number;
    inserted: number;
    qualified: number;
    skippedExisting: number;
  };
}

export interface UpdateDiscordProspectInput {
  status?: DiscordProspectStatus;
  notes?: string;
  ownerContact?: DiscordOwnerContact;
  draftedOutreach?: string;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchDiscordOverview(): Promise<DiscordOverviewResponse> {
  const r = await client.get<DiscordOverviewResponse>(
    '/discord-prospects/overview'
  );
  return r.data;
}

export async function fetchDiscordConfig(): Promise<DiscordConfigResponse> {
  const r = await client.get<DiscordConfigResponse>('/discord-prospects/config');
  return r.data;
}

export async function fetchDiscordProspects(
  params: DiscordProspectListParams
): Promise<DiscordProspectListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  const r = await client.get<DiscordProspectListResponse>('/discord-prospects', {
    params: cleanParams
  });
  return r.data;
}

export async function updateDiscordProspect(
  id: string,
  input: UpdateDiscordProspectInput
): Promise<DiscordProspect> {
  const r = await client.patch<DiscordProspect>(
    `/discord-prospects/${id}`,
    input
  );
  return r.data;
}

export async function runDiscordScrape(
  input: DiscordScrapeInput
): Promise<DiscordScrapeResult> {
  const r = await client.post<DiscordScrapeResult>(
    '/discord-prospects/scrape',
    input
  );
  return r.data;
}

export async function rescoreDiscordProspects(): Promise<{ ok: boolean }> {
  const r = await client.post<{ ok: boolean }>('/discord-prospects/rescore');
  return r.data;
}
